import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAuth, useTellescopeInfiniteQuery, useTellescopeMutation } from "@tellescope/tanstack-query";
import { buildCalendarMdbFilter, type CalendarEventMdbFilter, type CalendarFilters } from "@tellescope/calendar";
import {
  compileFreeformFilter,
  NO_VALUE_OPERATIONS,
  type FreeformFieldOption,
  type FreeformFilter,
  type FreeformFilterBlock,
  type FreeformOperation,
} from "@tellescope/utils";
import { toast } from "sonner";
import { BlockProps } from "../types";
import { useBlock } from ".";

/** The freeform filter fields the Calendar block exposes. */
export const CALENDAR_FILTER_FIELDS = {
  template: "templateId",
  location: "locationId",
  user: "attendees",
  cancelled: "cancelledAt",
  noShow: "noShowedAt",
  rescheduled: "rescheduledAt",
} as const;

const STATUS_FIELDS: string[] = [
  CALENDAR_FILTER_FIELDS.cancelled,
  CALENDAR_FILTER_FIELDS.noShow,
  CALENDAR_FILTER_FIELDS.rescheduled,
];

const isEquals = (operation: FreeformOperation) => operation === "equals";
const isNotEquals = (operation: FreeformOperation) => operation === "does_not_equal";

/**
 * The `mdbFilter` fragment matching one condition, or `undefined` for anything that does not compile
 * (an unsupported operation, or a missing value).
 *
 * Each field needs its own array-aware shape: `templateId` is a scalar and `locationIds` an array
 * (both `$in`/`$nin`), while `attendees` holds objects, so it needs `$elemMatch` on user-type entries
 * — and `$not: { $elemMatch }` to negate, so an event drops out when it has ANY de-selected user.
 *
 * The status fields carry `is_set`/`is_not_set` and compile through `compileFreeformFilter`, the same
 * function `buildCalendarMdbFilter` uses for the `customBlocks` path, so a status condition means the
 * same thing whether it is unioned here or AND-ed server-side.
 */
const compileCalendarCondition = (filter: FreeformFilter): CalendarEventMdbFilter | undefined => {
  if (STATUS_FIELDS.includes(filter.field)) {
    return NO_VALUE_OPERATIONS.includes(filter.operation) ? compileFreeformFilter(filter) : undefined;
  }
  const ids = filter.value;
  if (!Array.isArray(ids) || !ids.length) return undefined;

  const negated = isNotEquals(filter.operation);
  if (!negated && !isEquals(filter.operation)) return undefined;

  if (filter.field === CALENDAR_FILTER_FIELDS.template) {
    return { templateId: negated ? { $nin: ids } : { $in: ids } };
  }
  if (filter.field === CALENDAR_FILTER_FIELDS.location) {
    return { locationIds: negated ? { $nin: ids } : { $in: ids } };
  }
  if (filter.field === CALENDAR_FILTER_FIELDS.user) {
    const elemMatch = { $elemMatch: { id: { $in: ids }, type: "user" } };
    return { attendees: negated ? { $not: elemMatch } : elemMatch };
  }
  return undefined;
};

/**
 * The single `$or` clause for a block whose conditions are alternatives rather than requirements, or
 * `undefined` when none of them compile.
 *
 * An "or" block cannot use the `templateIds`/`locationIds`/`userIds` fields of {@link CalendarFilters}
 * — those compile to separate top-level keys, which a server-side filter combines with implicit AND.
 * For the same reason status conditions must be unioned here too, rather than travelling as the
 * `customBlocks` block the "and" path uses: that block compiles to its own clause, so combining it with
 * this one would require both halves and turn "any of" into "all of".
 */
export const calendarFilterBlockToOrClause = (block: FreeformFilterBlock): CalendarEventMdbFilter | undefined => {
  const alternatives = block.filters.reduce<CalendarEventMdbFilter[]>((acc, filter) => {
    const compiled = compileCalendarCondition(filter);
    return compiled ? [...acc, compiled] : acc;
  }, []);

  return alternatives.length ? { $or: alternatives } : undefined;
};

/**
 * Translates the block's persisted {@link FreeformFilterBlock} into the calendar feature's
 * {@link CalendarFilters}, so the reused {@link buildCalendarMdbFilter} can compile it into a
 * server-side `mdbFilter`. Pure and hook-free so it can be unit-tested in isolation. Covers only the
 * pieces `buildCalendarMdbFilter` supports natively — the `does_not_equal` (exclusion) conditions
 * are produced separately by {@link calendarFilterBlockToExtraClauses} and merged in the hook.
 *
 * - Template / Location / User `equals` → `templateIds` / `locationIds` / `userIds`, which compile
 *   to `$in` (and, for User, an `$elemMatch` on user-type attendees). These express AND only, so an
 *   "or" block leaves them unset and travels as the `$or` clause from
 *   {@link calendarFilterBlockToOrClause} instead.
 * - The status fields (`cancelledAt`/`noShowedAt`/`rescheduledAt`) keep their raw `is_set`/
 *   `is_not_set` conditions in a `customBlocks` freeform block, which `buildCalendarMdbFilter`
 *   compiles server-side (`is_not_set` → `{ $in: [null, ""] }`, `is_set` → `{ $exists, $nin }`),
 *   supporting both "only" and "hide" intents. An "or" block leaves `customBlocks` unset for the same
 *   reason it leaves the id fields unset — the block would compile to a clause AND-ed against the
 *   union — so its status conditions join the `$or` from {@link calendarFilterBlockToOrClause} instead.
 * - `enduserId` is always injected as an `enduserIds` attendee constraint, whatever the block's logic,
 *   so the block only ever shows the current contact's events.
 *
 * Conditions missing their value (an incomplete row) contribute nothing.
 */
export const calendarFilterBlockToFilters = (block: FreeformFilterBlock, enduserId: string): CalendarFilters => {
  const statusFilters = block.filters.filter((filter) => STATUS_FIELDS.includes(filter.field));
  const isOr = block.logic === "or";

  const filters = block.filters.reduce<CalendarFilters>(
    (acc, filter) => {
      if (isOr || STATUS_FIELDS.includes(filter.field) || !isEquals(filter.operation)) return acc;
      const ids = filter.value;
      if (!Array.isArray(ids) || !ids.length) return acc;

      if (filter.field === CALENDAR_FILTER_FIELDS.template) return { ...acc, templateIds: ids };
      if (filter.field === CALENDAR_FILTER_FIELDS.location) return { ...acc, locationIds: ids };
      if (filter.field === CALENDAR_FILTER_FIELDS.user) return { ...acc, userIds: ids };
      return acc;
    },
    { enduserIds: [enduserId] }
  );

  if (statusFilters.length && !isOr) {
    filters.customBlocks = [{ id: `${block.id}-status`, logic: block.logic, filters: statusFilters }];
  }

  return filters;
};

/**
 * Produces the server-side `$and` clauses for the block's `does_not_equal` (exclusion) conditions,
 * which {@link CalendarFilters}/{@link buildCalendarMdbFilter} can't express. Each field needs its
 * own array-aware negation: `templateId` is a scalar and `locationIds` an array (both use `$nin`),
 * while `attendees` needs `$not: { $elemMatch }` so an event is excluded when it has ANY of the
 * de-selected users. Pure and hook-free so it can be unit-tested in isolation.
 */
export const calendarFilterBlockToExtraClauses = (block: FreeformFilterBlock): CalendarEventMdbFilter[] => {
  if (block.logic === "or") return [];

  return block.filters.reduce<CalendarEventMdbFilter[]>((clauses, filter) => {
    if (!isNotEquals(filter.operation)) return clauses;
    const compiled = compileCalendarCondition(filter);
    return compiled ? [...clauses, compiled] : clauses;
  }, []);
};

/**
 * The complete server-side `mdbFilter` for the block: the natively-supported {@link CalendarFilters}
 * compiled by `buildCalendarMdbFilter`, plus the clauses that type cannot express — the "and" block's
 * exclusions, or the "or" block's single union. Pure and hook-free so it can be unit-tested in isolation.
 */
export const calendarFilterBlockToMdbFilter = (
  block: FreeformFilterBlock,
  enduserId: string,
  currentUserId: string | undefined
): CalendarEventMdbFilter => {
  const base = buildCalendarMdbFilter(calendarFilterBlockToFilters(block, enduserId), currentUserId);
  const orClause = block.logic === "or" ? calendarFilterBlockToOrClause(block) : undefined;
  const extraClauses = [...calendarFilterBlockToExtraClauses(block), ...(orClause ? [orClause] : [])];
  if (!extraClauses.length) return base;
  return { ...base, $and: [...(base.$and ?? []), ...extraClauses] };
};

export interface UseCalendarBlockArgs extends BlockProps {}

export const useCalendarBlock = (args: UseCalendarBlockArgs) => {
  const { t } = useTranslation("calendar");
  const { currentUser } = useAuth();
  const block = useBlock({ ...args, type: "calendar.calendar" });

  const filterBlock = useMemo<FreeformFilterBlock>(
    () =>
      block.parsedInfo?.filter ?? {
        id: args.blockId ?? `${args.viewId}-${args.blockIndex}`,
        logic: "and",
        filters: [],
      },
    [block.parsedInfo?.filter, args.blockId, args.viewId, args.blockIndex]
  );

  const mdbFilter = useMemo(
    () => calendarFilterBlockToMdbFilter(filterBlock, args.enduserId, currentUser?.id),
    [filterBlock, args.enduserId, currentUser?.id]
  );

  const {
    data: events,
    isLoading,
    fetchNextPage,
  } = useTellescopeInfiniteQuery({
    model: "calendar_events",
    method: "getSome",
    request: { mdbFilter },
    enabled: !!args.enduserId,
    select: (data) => data.pages.flat(),
  });

  const filterFieldOptions = useMemo<FreeformFieldOption[]>(
    () => [
      {
        value: CALENDAR_FILTER_FIELDS.template,
        label: t("Template"),
        valueType: "tags",
        operations: ["equals", "does_not_equal"],
      },
      {
        value: CALENDAR_FILTER_FIELDS.location,
        label: t("Location"),
        valueType: "tags",
        operations: ["equals", "does_not_equal"],
      },
      {
        value: CALENDAR_FILTER_FIELDS.user,
        label: t("User"),
        valueType: "tags",
        operations: ["equals", "does_not_equal"],
      },
      {
        value: CALENDAR_FILTER_FIELDS.cancelled,
        label: t("Cancelled"),
        valueType: "date",
        operations: ["is_set", "is_not_set"],
      },
      {
        value: CALENDAR_FILTER_FIELDS.noShow,
        label: t("No show"),
        valueType: "date",
        operations: ["is_set", "is_not_set"],
      },
      {
        value: CALENDAR_FILTER_FIELDS.rescheduled,
        label: t("Rescheduled"),
        valueType: "date",
        operations: ["is_set", "is_not_set"],
      },
    ],
    [t]
  );

  const onFilterChange = useCallback(
    (nextBlock: FreeformFilterBlock) => {
      block.updateInfo({ ...block.parsedInfo, id: block.parsedInfo?.id ?? crypto.randomUUID(), filter: nextBlock });
    },
    [block]
  );

  const updateEventMutation = useTellescopeMutation({
    model: "calendar_events",
    method: "updateOne",
    onError: () => {
      toast.error(t("Error updating appointment. Please try again."));
    },
  });

  const onCancel = useCallback(
    (eventId: string) => {
      if (!currentUser) return;
      updateEventMutation.mutate({
        id: eventId,
        cancelledAt: new Date(),
        cancelReason: "Unknown",
        statusChangeSource: { identifier: currentUser.id, source: "user" },
      });
    },
    [currentUser, updateEventMutation]
  );

  const onNoShow = useCallback(
    (eventId: string) => {
      updateEventMutation.mutate({ id: eventId, noShowedAt: new Date() });
    },
    [updateEventMutation]
  );

  const onComplete = useCallback(
    (eventId: string) => {
      if (!currentUser) return;
      updateEventMutation.mutate({ id: eventId, completedAt: new Date(), completedBy: currentUser.id });
    },
    [currentUser, updateEventMutation]
  );

  return {
    ...block,
    enduserId: args.enduserId,
    events: events ?? [],
    isLoading,
    fetchNextPage,
    filterBlock,
    filterFieldOptions,
    onFilterChange,
    onCancel,
    onNoShow,
    onComplete,
  };
};
